// A ~60-line translation layer instead of i18next.
//
// The app has one screen, two locales and about 150 strings; a full i18n
// framework brings a loader, a plural engine, an interpolation grammar and a
// bundle none of that earns here. What it would buy -- typed keys and a
// compile error on a missing translation -- is already covered by typing the
// Spanish dictionary against the English one.
//
// English is the default. Not the browser's language: the reader can switch in
// one click and the choice sticks, whereas a browser-driven default surprises
// whoever set their phone to something they do not actually want to read.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { en, type Key } from "./en";

export type { Key };
import { es } from "./es";

export type Lang = "en" | "es";

export const LANGS: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

const DICTS: Record<Lang, Record<Key, string>> = { en, es };
const STORAGE_KEY = "crikea.lang";

function stored(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "en" || v === "es") return v;
  } catch {
    // Private windows and blocked site data throw on access. English is the
    // default anyway, so there is nothing to recover.
  }
  return "en";
}

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  /** Translate a key, substituting {name} placeholders from `vars`. */
  t: (key: Key, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<Ctx | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(stored);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // The choice still applies to this session; it just will not survive a
      // reload. Better than refusing to switch at all.
    }
  }, []);

  const t = useCallback<Ctx["t"]>(
    (key, vars) => {
      const raw = DICTS[lang][key] ?? en[key] ?? key;
      if (!vars) return raw;
      return raw.replace(/\{(\w+)\}/g, (m, name) =>
        name in vars ? String(vars[name]) : m);
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n used outside I18nProvider");
  return ctx;
}

/** Shorthand for the common case of only needing the translate function. */
export function useT() {
  return useI18n().t;
}

/**
 * errorKey maps an API error code to a dictionary key, falling back to a
 * generic message. The server's English text is deliberately ignored when a
 * code is present: showing it would leak English into a Spanish UI.
 */
export function errorKey(code: string | undefined): Key {
  const key = `error.${code}` as Key;
  return code && key in en ? key : "error.unknown";
}
